import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFriendDuel } from "../context/FriendDuelContext";
import { friendDuelAPI } from "../api";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;
const DEFAULT_IMG = "https://avtomind.uz/storage/photos/default.webp";

const photoSrc = (photo: string) => {
  if (!photo) return DEFAULT_IMG;
  if (photo.startsWith("http")) return photo;
  return `${IMG_BASE}/${photo}`;
};

const useCountdown = (expiresAt: string) => {
  const [left, setLeft] = React.useState(() =>
    Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)),
  );
  React.useEffect(() => {
    const id = setInterval(() => {
      setLeft(Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const m = Math.floor(left / 60).toString().padStart(2, "0");
  const s = (left % 60).toString().padStart(2, "0");
  return { label: `${m}:${s}`, expired: left === 0 };
};

// ─── Single invite card ───────────────────────────────────────────────────────
const InviteToast: React.FC<{
  invite: ReturnType<typeof useFriendDuel>["incomingInvites"][0];
  onAccept: () => void;
  onDecline: () => void;
  accepting: boolean;
}> = ({ invite, onAccept, onDecline, accepting }) => {
  const { label, expired } = useCountdown(invite.expiresAt);

  return (
    <div
      style={{
        background: "#111827",
        border: "1px solid rgba(139,92,246,.5)",
        borderRadius: 14,
        padding: "12px 14px",
        boxShadow: "0 8px 32px rgba(0,0,0,.5)",
        width: 300,
        animation: "slideIn .25s ease",
      }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            border: "2px solid rgba(139,92,246,.5)",
          }}>
          <img
            src={photoSrc(invite.inviterPhoto)}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { e.currentTarget.src = DEFAULT_IMG; }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {invite.inviterName}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 11 }}>
            {invite.questionCount} savollik test taklifi
          </div>
        </div>
        <div
          style={{
            color: expired ? "#EF4444" : "#F59E0B",
            fontSize: 11,
            fontWeight: 800,
            background: expired ? "rgba(239,68,68,.1)" : "rgba(245,158,11,.1)",
            borderRadius: 6,
            padding: "3px 7px",
            whiteSpace: "nowrap",
          }}>
          ⏱ {label}
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onDecline}
          style={{
            flex: 1,
            padding: "9px 0",
            borderRadius: 10,
            border: "1px solid rgba(239,68,68,.3)",
            background: "rgba(239,68,68,.08)",
            color: "#f87171",
            fontWeight: 700,
            fontSize: 12,
            cursor: "pointer",
          }}>
          Rad etish
        </button>
        <button
          onClick={onAccept}
          disabled={accepting || expired}
          style={{
            flex: 2,
            padding: "9px 0",
            borderRadius: 10,
            border: "none",
            background:
              accepting || expired ?
                "rgba(139,92,246,.3)"
              : "linear-gradient(135deg, #8B5CF6, #6D28D9)",
            color: "#fff",
            fontWeight: 800,
            fontSize: 12,
            cursor: accepting || expired ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}>
          {accepting ?
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,.3)",
                borderTop: "2px solid #fff",
                animation: "spin 1s linear infinite",
              }}
            />
          : expired ? "Muddati tugadi"
          : "✅ Qabul qilish"}
        </button>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const FriendInviteNotification: React.FC = () => {
  const navigate = useNavigate();
  const { incomingInvites, removeInvite } = useFriendDuel();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  if (incomingInvites.length === 0) return null;

  const handleAccept = async (inviteId: string) => {
    setAcceptingId(inviteId);
    try {
      const res = await friendDuelAPI.accept(inviteId);
      removeInvite(inviteId);
      navigate("/friend-duel", {
        state: {
          role: "invitee",
          sessionId: res.data.sessionId,
          questions: res.data.questions,
          opponentName: res.data.opponent?.name || res.data.opponentName || "Do'stingiz",
          opponentPhoto: res.data.opponent?.photo || res.data.opponentPhoto || "",
          totalQuestions: res.data.totalQuestions,
        },
      });
    } catch {
      setAcceptingId(null);
    }
  };

  const handleDecline = async (inviteId: string) => {
    try {
      await friendDuelAPI.decline(inviteId);
    } catch {}
    removeInvite(inviteId);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 8,
        pointerEvents: "none",
      }}>
      {/* Collapse/expand badge */}
      <button
        onClick={() => setCollapsed((p) => !p)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "linear-gradient(135deg, #8B5CF6, #6D28D9)",
          border: "none",
          borderRadius: 20,
          padding: "7px 12px",
          color: "#fff",
          fontWeight: 800,
          fontSize: 13,
          cursor: "pointer",
          boxShadow: "0 4px 16px rgba(139,92,246,.5)",
          pointerEvents: "auto",
          animation: "pulse-glow 2s ease-in-out infinite",
        }}>
        <span style={{ fontSize: 16 }}>👥</span>
        <span>{incomingInvites.length} ta taklif</span>
        <span style={{ fontSize: 10, opacity: 0.8 }}>{collapsed ? "▼" : "▲"}</span>
        {/* Red dot badge */}
        <div
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#EF4444",
            border: "2px solid #111827",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 900,
          }}>
          {incomingInvites.length}
        </div>
      </button>

      {/* Invite cards (expanded) */}
      {!collapsed && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            pointerEvents: "auto",
          }}>
          {incomingInvites.map((inv) => (
            <InviteToast
              key={inv.inviteId}
              invite={inv}
              accepting={acceptingId === inv.inviteId}
              onAccept={() => handleAccept(inv.inviteId)}
              onDecline={() => handleDecline(inv.inviteId)}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes pulse-glow {
          0%,100% { box-shadow: 0 4px 16px rgba(139,92,246,.5); }
          50%      { box-shadow: 0 4px 24px rgba(139,92,246,.9); }
        }
      `}</style>
    </div>
  );
};

export default FriendInviteNotification;
